import {AGGREGATIONS, Calculation, Column, Filter, FILTER_OPERATORS, GRAINS, ReportQuery,
        Sort, TABLE_CALCULATIONS} from "./querySpec";
import {Dataset, DatasetField} from "./reportApi";

/**
 * The shelves a person arranged, as the specification the server compiles.
 *
 * THIS IS THE WHOLE BUILDER, as far as anything testable goes. The panels are arrangement; what a
 * report MEANS is decided here, and it is decided without a grid, an axios or a data type -- so it
 * can be tested at the speed of arithmetic rather than through a rendered page.
 *
 * WHICH SHELF A FIELD MAY GO ON IS NOT A PREFERENCE. A dimension slices and a measure aggregates,
 * and that is what makes direct manipulation possible at all in QuickSight, Tableau and Power BI:
 * the tool knows what a field IS. We know it better than they do -- the role comes from the field's
 * governed data type rather than from sniffing values, which is how those tools end up offering to
 * sum an identifier.
 *
 * A COUNT IS THE EXCEPTION, and it is the one people reach for first. "How many work instructions
 * per department" counts a DIMENSION. So a dimension may be dropped on the values shelf provided
 * the aggregation is count or countDistinct; anything else refuses, by name.
 */

export type Shelf = {
    /** The field's `code`, as the dataset names it. Absent on a calculated shelf. */
    field: string;
    /** Arithmetic over fields, instead of a field. Values shelf only. */
    calculation?: Calculation;
    /** Measures only. */
    aggregation?: string;
    /** A window over the finished rows. Measures only. */
    tableCalculation?: string;
    /** Dimensions the window is scoped to -- each row against its own group. */
    within?: string[];
    /** Dimensions only: the grain to group at. */
    grain?: string;
};

export type Shelves = {
    dataset: string;
    /** What one row of the result is. Absent means "summary". */
    shape?: "summary" | "detail";
    rows: Shelf[];
    values: Shelf[];
    filters: Filter[];
    sort?: Sort[];
    limit?: number;
};

export type Refusal = {where: string; why: string};

const NUMERIC_ONLY = ["sum", "avg", "median", "percentile", "stdev", "variance"];
const COUNTING = ["count", "countDistinct"];

function fieldOf(dataset: Dataset | undefined, code: string): DatasetField | undefined {
    return (dataset?.Fields ?? []).find(one => one.Field === code);
}

function known(entries: {id: string; phase: string}[], id: string | undefined): boolean {
    return !!entries.find(one => one.id === id && one.phase === "v1");
}

/**
 * What is wrong with these shelves, in the words a person can act on.
 *
 * COLLECTED, NOT THROWN ON THE FIRST. Somebody who dropped three things in the wrong place should
 * be told about three, not led through them one refusal at a time.
 *
 * THE SERVER REFUSES ALL OF THIS TOO, and that is the point rather than a duplication: this is the
 * builder declining to offer what the compiler would decline to accept, which is a different job
 * from enforcement. If the two ever disagree, the server wins and the reader sees its reason.
 */
export function refusals(shelves: Shelves, dataset: Dataset | undefined): Refusal[] {
    const out: Refusal[] = [];

    if (!dataset) return [{where: "Dataset", why: "Choose something to report on"}];
    if (!shelves.rows.length && !shelves.values.length) {
        return [{where: "Shelves", why: "Add at least one field"}];
    }

    for (const shelf of shelves.rows) {
        const field = fieldOf(dataset, shelf.field);
        if (!field) { out.push({where: shelf.field, why: "not a field of this dataset"}); continue; }
        if (shelf.grain && !known(GRAINS, shelf.grain)) {
            out.push({where: field.Label, why: `cannot group by ${shelf.grain} yet`});
        }
    }

    // A LISTING HAS NOTHING TO SUMMARISE OVER. Said once, about the shelf, rather than once per
    // field on it -- three fields in the wrong place is one mistake, not three.
    if (shelves.shape === "detail" && shelves.values.length) {
        out.push({where: "Summarize",
                  why: "a listing shows records; switch to one row per group to summarize"});
    }
    if (shelves.shape === "detail" && (shelves.sort ?? []).some(one => one.aggregation)) {
        out.push({where: "Sort", why: "sorting by a total needs one row per group"});
    }
    if (shelves.shape === "detail" && shelves.filters.some(one => (one as any).afterGrouping)) {
        out.push({where: "Filters", why: "filtering on a total needs one row per group"});
    }

    for (const shelf of shelves.values) {
        if (shelf.calculation) {
            // THE SERVER CHECKS THE TREE. Re-implementing that here would be a second answer to the
            // same question, and the two would disagree exactly where somebody is experimenting.
            // What the builder can usefully say is whether it has been finished.
            if (!shelf.calculation.label?.trim()) {
                out.push({where: "Calculation", why: "needs a name"});
            }
            if (!known(AGGREGATIONS, shelf.aggregation)) {
                out.push({where: shelf.calculation.label || "Calculation",
                          why: `${shelf.aggregation ?? "that"} is not available yet`});
            }
            continue;
        }
        const field = fieldOf(dataset, shelf.field);
        if (!field) { out.push({where: shelf.field, why: "not a field of this dataset"}); continue; }
        if (!known(AGGREGATIONS, shelf.aggregation)) {
            out.push({where: field.Label, why: `${shelf.aggregation ?? "that"} is not available yet`});
            continue;
        }
        // A SUM OF TEXT IS NOT A THING. The server would refuse it too -- by failing to cast, which
        // arrives as a database error rather than as an explanation.
        if (field.Role === "dimension" && !COUNTING.includes(shelf.aggregation!)) {
            out.push({where: field.Label,
                      why: `is not a number, so it can only be counted`});
        }
        if (field.Role === "measure" && !NUMERIC_ONLY.concat(COUNTING).includes(shelf.aggregation!)) {
            out.push({where: field.Label, why: `cannot be summarized that way`});
        }
    }

    // THE ORDERED TABLE CALCULATIONS NEED A SORT, and the builder says so before the server has to.
    // A running total down an arbitrary order is a number that disagrees with the rows beside it.
    for (const shelf of shelves.values) {
        if (!shelf.tableCalculation) continue;
        const entry = TABLE_CALCULATIONS.find(one => one.id === shelf.tableCalculation);
        if (!entry || entry.phase !== "v1") {
            out.push({where: "Table calculation",
                      why: `${shelf.tableCalculation} is not available yet`});
            continue;
        }
        if (entry.needsSort && !(shelves.sort ?? []).length) {
            out.push({where: entry.label, why: "needs the report sorted, so it knows which way to run"});
        }
        // SCOPED TO A GROUP THE REPORT ACTUALLY HAS. Said here as well as by the server, because it
        // is a mistake somebody makes while rearranging shelves rather than while composing a spec.
        for (const field of shelf.within ?? []) {
            if (!shelves.rows.some(one => one.field === field)) {
                out.push({where: entry.label,
                          why: `is scoped to ${field}, which this report no longer slices by`});
            }
        }
    }

    for (const filter of shelves.filters) {
        const code = filter.of?.field;
        if (!fieldOf(dataset, code)) { out.push({where: code, why: "not a field of this dataset"}); continue; }
        if (!known(FILTER_OPERATORS, filter.operator)) {
            out.push({where: code, why: `"${filter.operator}" is not available yet`});
            continue;
        }
        // Said here rather than left to a refusal after Run: an empty box beside "is in the last"
        // is a filter nobody has finished, not one that is wrong.
        if (filter.operator === "relativeDate"
                && !/^\d+$/.test(String(filter.value ?? "").trim())) {
            out.push({where: code, why: "needs a number of periods"});
        }
    }

    return out;
}

/**
 * The specification, or null when the shelves do not yet describe one.
 *
 * DIMENSIONS FIRST, then measures, because that is the order the result reads in and the order the
 * server groups by. Rearranging the shelves rearranges the report, which is the point.
 */
export function buildQuery(shelves: Shelves, dataset: Dataset | undefined): ReportQuery | null {
    if (refusals(shelves, dataset).length) return null;

    const columns: Column[] = [
        ...shelves.rows.map(shelf => ({
            of: {dataset: shelves.dataset, field: shelf.field},
            role: "dimension" as const,
            ...(shelf.grain ? {grain: shelf.grain} : {}),
            label: fieldOf(dataset, shelf.field)?.Label,
        })),
        ...shelves.values.map(shelf => shelf.calculation
            ? {
                calculation: shelf.calculation,
                role: "measure" as const,
                aggregation: shelf.aggregation,
                ...(shelf.tableCalculation ? {tableCalculation: shelf.tableCalculation} : {}),
                label: shelf.calculation.label,
            }
            : {
                of: {dataset: shelves.dataset, field: shelf.field},
                role: "measure" as const,
                aggregation: shelf.aggregation,
                ...(shelf.tableCalculation ? {tableCalculation: shelf.tableCalculation} : {}),
                ...(shelf.within?.length ? {within: shelf.within} : {}),
                label: fieldOf(dataset, shelf.field)?.Label,
            }),
    ];

    // A RELATIVE DATE'S AMOUNT IS A NUMBER, and the text field produces a string. The server
    // refuses a non-number outright rather than coercing -- correctly, since a string there could
    // carry anything -- so the conversion belongs here, where the input actually was text.
    const filters = shelves.filters.map(one => one.operator === "relativeDate"
        ? {...one, unit: one.unit ?? "day", value: Number(one.value)}
        : one);

    return {
        version: 1,
        dataset: shelves.dataset,
        // OMITTED WHEN IT IS THE DEFAULT, so a summary report's specification is byte-identical to
        // the one it would have been before this shape existed -- which matters because saved
        // reports are compared as JSON to decide whether they are unsaved.
        ...(shelves.shape === "detail" ? {shape: "detail" as const} : {}),
        columns,
        ...(filters.length ? {filters} : {}),
        ...(shelves.sort?.length ? {sort: shelves.sort} : {}),
        ...(shelves.limit ? {limit: shelves.limit} : {}),
    };
}

/**
 * The aggregations worth offering for a field, given what it is.
 *
 * OFFERED, NOT FILTERED SILENTLY. A measure gets the numeric summaries and the counts; a dimension
 * gets the counts only. Showing a greyed Sum beside a text field and showing nothing at all are
 * different messages, and the first one is true.
 */
export function aggregationsFor(field: DatasetField | undefined): {id: string; label: string}[] {
    const usable = AGGREGATIONS.filter(one => one.phase === "v1");
    if (!field) return [];
    if (field.Role !== "measure") return usable.filter(one => COUNTING.includes(one.id));

    // SUM FIRST, BECAUSE THE FIRST IS THE DEFAULT. A field dropped on the values shelf takes
    // aggregationsFor(...)[0], and the vocabulary's own order starts at Count -- so dropping
    // "Review Period (months)" offered to COUNT it. Counting a number is occasionally what somebody
    // wants and almost never what they meant, and the wrongness is quiet: the report runs and
    // returns a plausible integer. Seen on the first screenshot of a real dataset.
    const first = ["sum", "avg"];
    return [...usable.filter(one => first.includes(one.id))
                     .sort((a, b) => first.indexOf(a.id) - first.indexOf(b.id)),
            ...usable.filter(one => !first.includes(one.id))];
}
